import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'
import type { KnowledgeFmt } from '@/i18n/messages/en/knowledge.ts'

/**
 * `knowledge` in Brazilian Portuguese. Declared, never inferred, so a gap fails `tsc`.
 *
 * Register is `você`, informal-professional (LOC-061). The ratified vocabulary of ADR-0018 is
 * binding here: *base de conhecimento* (f.) — never *fonte*, never *repositório*; *documento*
 * (m.); *trecho* (m.) — never *pedaço*; *agente* (m.); *grupo* (m.) — never *time*; *pessoa* (f.);
 * *pasta conectada* (f.); *solicitação de compartilhamento* (f.) — never *partilha*. The bound
 * phrases of D-37 keep their ratified Portuguese: *Quem pode usar*, *Pertence a*, *Enviar para
 * revisão*, *Aguardando revisão*, *Agentes que usam*, *todas as pessoas que entram*.
 *
 * ── Agreement, and where the English had to be restructured ──
 *
 * A *base de conhecimento* is feminine and an *agente* is masculine, so the participles follow
 * the noun they describe: "Compartilhada", "Mantido". Three English phrases had no correct
 * Portuguese without a change of shape, and each is rewritten to be gender- and number-neutral
 * rather than guessed at (PRD-27 D-21): *only its owner* and *you can open it as an
 * administrator* name a person whose gender is unknown, and *Only X can use it* takes a verb
 * that agrees in number with a subject the caller chooses. They are marked below.
 *
 * ── Terms of art left in English (D-15) ──
 *
 * `OCR`, `embedding`, `parser`, `pipeline`, `hash`, `status`, `slide`, `tok`. A Brazilian
 * engineer reads every one of these in English in the tools they already use, and "incorporação"
 * for an embedding is less clear, not more.
 */

const isOne = (fmt: KnowledgeFmt, count: number): boolean => pluralCategory(fmt.locale, count) === 'one'

const people = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'pessoa' : 'pessoas'}`
const documents = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'documento' : 'documentos'}`
const bases = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'base de conhecimento' : 'bases de conhecimento'}`
const passages = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'trecho' : 'trechos'}`

/**
 * The same list `SUPPORTED_FORMATS_SENTENCE` composes in `@ralph/contracts`, said in Portuguese.
 *
 * Nine of the eleven labels are format names that never translate (D-17); only *images* and
 * *plain text* are common nouns, and the contract's hardcoded " and " is English. Written out
 * here until F-16 moves the list itself behind a locale-aware join.
 */
const FORMATS = 'PDF, Word, PowerPoint, Excel, imagens, Markdown, HTML, JSON, CSV e texto simples'

/**
 * Deixar um agente responder a partir de uma base, dito de cinco maneiras (PRD-26 D-16) — e o
 * registro disso (PRD-27 D-7).
 *
 * Funções do módulo, porque `lend.disclosure` compõe as mesmas cinco frases que as entradas
 * vizinhas expõem uma a uma. O diálogo e a releitura da auditoria rodam uma implementação só, e
 * é isso que garante que o registro e a evidência digam a mesma coisa.
 */
// "quem gerencia X" evita escolher entre *gestor* e *gestora* para alguém que ninguém conhece.
const lendMaintainedBy = (agent: string, owner: { kind: 'user' | 'group'; name: string }): string =>
  owner.kind === 'group'
    ? `${agent} é mantido por quem gerencia ${owner.name}.`
    : `${agent} é mantido por ${owner.name}.`

const LEND_ACTS = 'Ele pode agir, além de responder — enviar, alterar ou criar coisas onde estiver conectado.'
const LEND_DELEGATES = 'Ele pode repassar perguntas a outros agentes.'
const LEND_ACTS_AND_DELEGATES =
  'Ele pode agir, além de responder — enviar, alterar ou criar coisas onde estiver conectado — e repassar perguntas a outros agentes.'
const LEND_ANSWERS_ONLY = 'Ele só responde; não pode agir.'

const lendCapability = (acts: boolean, delegates: boolean): string =>
  acts && delegates ? LEND_ACTS_AND_DELEGATES : acts ? LEND_ACTS : delegates ? LEND_DELEGATES : LEND_ANSWERS_ONLY

/** "1 delas não pode" e "12 delas não podem": o verbo concorda com a contagem (D-19). */
const lendReachNow = (fmt: KnowledgeFmt, reach: number, beyond: number, base: string): string =>
  reach === 0
    ? 'Ele ainda não foi compartilhado com ninguém, então hoje não alcança ninguém.'
    : beyond === 0
      ? `Ele alcança ${people(fmt, reach)} hoje, e todas já podem usar ${base}.`
      : `Ele alcança ${people(fmt, reach)} hoje; ${fmt.formatCount(beyond)} ${isOne(fmt, beyond) ? 'delas não pode' : 'delas não podem'} usar ${base} por conta própria.`

const lendReachNotLive = (base: string): string =>
  `Ele não está em uso agora, então hoje não alcança ninguém. Quando estiver, todas as pessoas que ele alcança recebem respostas de ${base} por meio dele.`

const lendGrows = (base: string): string =>
  `O alcance dele pode crescer sem pedir nada a você: quando quem mantém o agente compartilhar com mais pessoas, elas também recebem respostas de ${base}.`

const LEND_RECALL = 'Você pode parar isto a qualquer momento. Parar não recolhe as respostas que ele já deu.'

export const knowledge: Messages['knowledge'] = {
  page: {
    title: 'Conhecimento',
    lead: 'As bases de conhecimento que você mantém e as que compartilharam com você. Um agente só pode citar o que está nelas.',
    newBase: 'Nova base de conhecimento',
    local: 'lido aqui · nada sai daqui',
    localHint:
      'Os documentos são lidos e indexados dentro desta instalação; nada do que há neles é enviado para outro lugar.',
    searchLabel: 'Buscar bases de conhecimento',
    searchPlaceholder: 'Busque por nome ou descrição',
    clearSearch: 'Limpar a busca',
    viewsLabel: 'Quais bases de conhecimento',
    views: { maintain: 'Você mantém', use: 'Você pode usar', all: 'Todas' },
    viewCount: (fmt, count) => bases(fmt, count),
    loading: 'Carregando bases de conhecimento…',
    failed: 'Não foi possível carregar as bases de conhecimento.',
    failedBody: 'O problema é na leitura da lista, não é uma lista vazia. Tente de novo em instantes.',
    empty: {
      maintain: 'Você ainda não mantém nenhuma base de conhecimento.',
      use: 'Ainda não compartilharam nada com você.',
      all: 'Ainda não há bases de conhecimento.'
    },
    emptyBody: {
      maintain:
        'Crie uma para os documentos que seus agentes devem citar. No começo só você pode usar; você decide quem mais pode.',
      use: 'Quando alguém compartilhar uma base de conhecimento com um grupo do qual você faz parte, ou com todas as pessoas, ela aparece aqui.',
      all: 'Quando alguém criar uma base de conhecimento, ela aparece aqui.'
    },
    noMatch: (q) => `Nenhuma base de conhecimento aqui corresponde a “${q}”.`,
    noMatchBody: 'Confira a grafia ou procure na outra lista.',
    pickOne: 'Escolha uma base de conhecimento',
    pickOneBody:
      'Veja o que ela tem, busque nela do jeito que um agente busca e — onde você a mantém — quem pode usar.',
    back: 'Todas as bases de conhecimento',
    deleted: (name) => `${name} foi excluída. Os agentes que usavam continuam funcionando sem ela.`,
    dismiss: 'Dispensar',
    signInFailed: 'Essa entrada não foi concluída.',
    signInFailedBody:
      'Nada foi conectado e nada aqui mudou. Abra a base de conhecimento à qual você estava conectando uma pasta e tente de novo.'
  },

  standing: {
    empty: 'Vazia',
    reading: 'Em leitura',
    silent: 'Nada respondendo',
    attention: 'Precisa de atenção',
    stale: 'Desatualizada',
    ready: 'Respondendo'
  },

  row: {
    noDocuments: 'Nenhum documento ainda',
    loadingFromFolder: 'Carregando de uma pasta conectada',
    folderNeedsPerson: (fmt, count) =>
      isOne(fmt, count)
        ? 'Uma pasta conectada precisa de atenção'
        : `${fmt.formatCount(count)} pastas conectadas precisam de atenção`,
    allAnswering: (fmt, count) =>
      isOne(fmt, count)
        ? `${fmt.formatCount(count)} documento, respondendo`
        : `${documents(fmt, count)}, todos respondendo`,
    someAnswering: (fmt, answering, total) => `${fmt.formatCount(answering)} de ${documents(fmt, total)} respondendo`,
    readingRest: (fmt, answering, total) =>
      answering === 0
        ? `Lendo ${documents(fmt, total)}…`
        : `${fmt.formatCount(answering)} de ${fmt.formatCount(total)} respondendo · lendo o restante`,
    nothingAnswering: (fmt, total) => `${documents(fmt, total)}, nenhum respondendo`,
    // `when` já chega do `RelativeTimeFormat` com a preposição ("há 3 sem."), por isso a frase
    // não acrescenta outra (LOC-022).
    notUpdated: (when) => `última adição ${when}`,
    folders: (fmt, count) =>
      isOne(fmt, count) ? `${fmt.formatCount(count)} pasta conectada` : `${fmt.formatCount(count)} pastas conectadas`,
    waitingForReview: 'aguardando revisão',
    requestsToDecide: (fmt, count) =>
      isOne(fmt, count)
        ? `${fmt.formatCount(count)} solicitação para decidir`
        : `${fmt.formatCount(count)} solicitações para decidir`
  },

  why: {
    onlyYou: 'Só você',
    yours: 'Sua',
    ownedBy: (owner) => `Pertence a ${owner}`,
    // RESTRUCTURED (D-21): "only its managers" / "only its owner" name people whose gender is
    // unknown, so both are said with a verb instead of a gendered noun — the same shape as the
    // ratified "Só quem mantém".
    onlyItsManagers: 'só quem gerencia',
    onlyItsOwner: 'só quem a mantém',
    sharedWith: (fmt, names, more) =>
      `compartilhada com ${fmt.formatList(more > 0 ? [...names, `mais ${fmt.formatCount(more)}`] : names)}`,
    sharedWithEveryone: 'compartilhada com todas as pessoas',
    sharedWithYou: (fmt, names, more) =>
      `Compartilhada com ${fmt.formatList(more > 0 ? [...names, `mais ${fmt.formatCount(more)}`] : names)}`,
    includesYou: (fmt, count) =>
      isOne(fmt, count)
        ? 'inclui você'
        : count === 2
          ? 'você está nos dois'
          : `você está em todos os ${fmt.formatCount(count)}`,
    everyone: 'Compartilhada com todas as pessoas',
    // RESTRUCTURED (D-21): "as an administrator" would force administrador/administradora on a
    // reader whose gender we do not know. The ROLE is masculine whoever holds it, so the
    // sentence names the access rather than the person — the same shape `people` uses.
    administrator: 'você pode abrir com acesso de administrador',
    notForYou: 'não compartilhada com você',
    // RESTRUCTURED (D-21): "Only X can use it" puts the verb after a subject the caller chooses —
    // "você" (singular) or "gestores de Finance EMEA" (plural) — and Portuguese inflects it.
    // Naming the audience instead agrees with nothing, and reuses the bound phrase.
    onlyCanUse: (who) => `Quem pode usar: ${who}`
  },

  base: {
    loading: 'Carregando a base de conhecimento…',
    missing: 'Esta base de conhecimento não está disponível para você.',
    missingBody:
      'Ela pode ter sido excluída, ou não está compartilhada com você. A lista mostra as que você pode abrir.',
    failed: 'Não foi possível carregar esta base de conhecimento.',
    failedBody: 'Tente de novo em instantes. Nada nela mudou.',
    noDescription: 'Sem descrição.',
    ownedBy: 'Pertence a',
    you: 'você',
    move: 'Mover…',
    editLabel: (name) => `Editar ${name}`,
    deleteLabel: (name) => `Excluir ${name}`,
    adminOnly:
      'Você só pode abrir isto com acesso de administrador. Abrir os documentos dela fica registrado na trilha de auditoria.',
    adminOnlyTitle: 'Não compartilhada com você',
    waiting: (fmt, names) =>
      names === null
        ? 'Aguardando revisão: compartilhar com todas as pessoas.'
        : `Aguardando revisão: compartilhar com ${fmt.formatList(names)}.`,
    requests: (fmt, count) =>
      isOne(fmt, count)
        ? 'Alguém pediu para deixar um agente responder a partir dela para todas as pessoas que ele alcança.'
        : `${fmt.formatCount(count)} pessoas pediram para deixar um agente responder a partir dela para todas as pessoas que ele alcança.`,
    stats: {
      documents: 'documentos',
      passages: 'trechos',
      space: 'espaço',
      added: 'última adição',
      never: 'nunca',
      of: (limit) => `de ${limit}`
    },
    freshness: 'atualizar a cada',
    pictures: 'imagens',
    counting: 'Contando com o que esta base de conhecimento pode responder…',
    census: (fmt, answering, total) =>
      total === 0
        ? 'Nada aqui ainda. Um agente perguntado sobre isso não vai encontrar nada para citar.'
        : answering === total
          ? isOne(fmt, total)
            ? `${fmt.formatCount(total)} documento respondendo.`
            : `Todos os ${fmt.formatCount(total)} documentos respondendo.`
          : answering === 0
            ? `${documents(fmt, total)}, e nenhum deles pode ser citado ainda.`
            : `${fmt.formatCount(answering)} de ${documents(fmt, total)} respondendo.`,
    window: {
      weeks: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'semana' : 'semanas'}`,
      days: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'dia' : 'dias'}`,
      hours: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'hora' : 'horas'}`
    }
  },

  tabs: {
    label: 'Visões da base de conhecimento',
    documents: 'Documentos',
    access: 'Quem pode usar',
    folders: 'Pastas conectadas',
    search: 'Busca',
    needsAttention: 'precisa de atenção',
    waiting: 'aguardando'
  },

  status: {
    pending: 'na fila',
    extracting: 'lendo',
    chunking: 'em trechos',
    embedding: 'embedding',
    indexed: 'indexado',
    failed: 'falhou',
    quarantined: 'em quarentena',
    deleted: 'retirado'
  },

  documents: {
    heading: 'Documentos',
    count: (fmt, shown, total, filtered) =>
      filtered ? `${fmt.formatCount(shown)} de ${documents(fmt, total)}` : documents(fmt, total),
    clearFilter: 'Limpar filtro',
    filterLabel: 'Filtrar documentos por nome',
    filterPlaceholder: 'Filtrar por nome…',
    loading: 'Carregando documentos…',
    failed: 'Não foi possível carregar os documentos.',
    empty: 'Nenhum documento ainda',
    emptyBody: (canMaintain) =>
      canMaintain
        ? `Adicione ${FORMATS}. Cada arquivo é lido e indexado assim que chega.`
        : 'Nada foi adicionado ainda. Quem mantém a base adiciona os documentos.',
    nothingMatches: 'Nada corresponde',
    nothingMatchesBody: 'Nenhum documento aqui corresponde a esse filtro.',
    columns: { document: 'Documento', status: 'Status', passages: 'Trechos', size: 'Tamanho', indexed: 'Indexado' },
    actions: 'Ações',
    newest:
      'Os 500 documentos mais recentes. Os mais antigos estão indexados e respondendo, e não aparecem nesta lista.',
    remove: (title) => `Remover ${title}`,
    removeTitle: (title) => `Remover “${title}”?`,
    // Sem artigo no singular: "O 1 trecho" lê mal em português, "1 trecho dele sai" não.
    removePassages: (fmt, count) =>
      count > 0
        ? `${
            isOne(fmt, count)
              ? `${fmt.formatCount(count)} trecho dele sai`
              : `Os ${fmt.formatCount(count)} trechos dele saem`
          } de toda resposta futura. As citações já dadas continuam funcionando e dizem que ele foi retirado.`
        : 'Ele não tem trechos, então nenhuma resposta muda.',
    removeFinal: 'Isto não pode ser desfeito por aqui.',
    removeForGood: 'Remover definitivamente',
    keep: 'Manter',
    removed: (title) => `“${title}” foi removido.`,
    notRemoved: (reason) => `Esse documento não foi removido. ${reason}`,
    fullReason: 'Motivo completo',
    couldNotRead: 'Não foi possível ler este arquivo.',
    parserWords: 'Nas palavras do leitor:',
    remedy: {
      scan_off:
        'Este arquivo tem imagens de texto em vez de texto, e ler imagens exige OCR. Mude como esta base de conhecimento lê imagens — ou, se disser que o OCR não está instalado, peça a quem administra — e depois leia o arquivo de novo por aqui.',
      scan_unreadable:
        'O OCR rodou e não encontrou nada legível: a digitalização está fraca demais, pequena demais ou torta. Digitalize de novo em resolução maior, ou adicione a versão de origem.',
      legacy:
        'Este é um formato antigo do Office. Abra no Office, salve como .docx, .xlsx ou .pptx e adicione essa cópia.',
      protected: 'O arquivo está protegido. Salve uma cópia sem proteção e adicione essa no lugar.',
      timeout:
        'O leitor ficou sem tempo neste arquivo — normalmente uma digitalização muito grande ou uma apresentação cheia de imagens. Leia de novo quando a fila estiver mais calma, divida o arquivo, ou peça a quem administra para aumentar o tempo de leitura se isso continuar acontecendo.',
      too_large:
        'O arquivo é maior do que o leitor aceita — normalmente uma imagem embutida muito grande ou uma planilha com milhões de células. Divida o arquivo, ou exporte só a parte que importa.',
      empty:
        'O arquivo foi lido sem erros e não tinha nada que valesse indexar — um documento vazio, ou um modelo sem texto no corpo. Confira se você adicionou o arquivo certo.',
      unsupported: `O leitor não trabalha com este formato. Converta para um destes — ${FORMATS} — e adicione de novo.`,
      lost: 'O arquivo foi aceito, mas o processo que lê ele parou antes de começar. Leia de novo por aqui.',
      superseded:
        'Outro envio assumiu este documento enquanto ele estava sendo lido, então esta linha é um resto. O envio mais novo é o que vale — remova este.'
    },
    noRemedy:
      'Nada sobre esta causa é reconhecido, então não há o que sugerir com segurança. As palavras do leitor estão abaixo.',
    quarantined: 'Retido pela verificação de malware.',
    quarantinedBody:
      'Ele nunca foi lido e nada dele foi indexado. Trate disso com quem cuida da segurança aqui, em vez de adicionar de novo.',
    silent: 'Indexado, e sem nenhum trecho.',
    silentBody:
      'A busca lê trechos, então nada neste arquivo pode ser citado. Adicione de novo; se ele chegar do mesmo jeito, não há texto nele que o leitor consiga alcançar.',
    noPassagesBadge: 'sem trechos',
    connectedFolder: 'pasta conectada',
    adminGateTitle: 'Os documentos dela ainda não estão abertos',
    adminGateBody:
      'Você não é uma das pessoas com quem ela foi compartilhada, então abrir os documentos dela fica registrado na trilha de auditoria.',
    adminGateOpen: 'Abrir os documentos'
  },

  upload: {
    add: 'Adicionar documentos',
    adding: (fmt, index, total, name) =>
      total === 1
        ? `Adicionando ${name}…`
        : `Adicionando ${fmt.formatCount(index)} de ${fmt.formatCount(total)} — ${name}…`,
    added: (fmt, count) =>
      isOne(fmt, count)
        ? `${fmt.formatCount(count)} documento adicionado.`
        : `${fmt.formatCount(count)} documentos adicionados.`,
    stillReading: 'Ainda em leitura — a lista diz quando cada um termina.',
    reused: (name) => `${name} já estava aqui, então nada novo foi guardado.`,
    unreadable: (name) => `${name} foi adicionado e não pôde ser lido — a linha abaixo diz por quê.`,
    quarantined: (name) => `${name} foi separado pela verificação de malware.`,
    notAdded: (name) => `${name} não foi adicionado.`,
    tooLarge: (name, limit) => `${name} é maior que ${limit}, o máximo que um arquivo pode ter.`,
    rateLimited: 'Arquivos demais de uma vez. Espere um pouco e adicione o restante de novo.',
    notTried: (fmt, count) =>
      isOne(fmt, count)
        ? 'O próximo arquivo não foi tentado.'
        : `Os ${fmt.formatCount(count)} arquivos seguintes não foram tentados.`,
    unchanged: 'Nada mais nesta base de conhecimento mudou.',
    clear: 'Limpar',
    limits: (fmt, count, limit, used, space) =>
      `${fmt.formatCount(count)} de ${fmt.formatCount(limit)} documentos · ${used} de ${space}`,
    full: 'Esta base de conhecimento está cheia. Exclua algo de que ela não precisa mais, ou peça a quem administra para aumentar o limite.'
  },

  reading: {
    title: 'Imagens e digitalizações',
    change: 'Alterar…',
    save: 'Salvar',
    saving: 'Salvando…',
    cancel: 'Cancelar',
    languages: 'Idiomas',
    languagesHint:
      'Nenhum escolhido significa todos, nesta ordem. Nomear só os idiomas em que os documentos estão deixa a leitura de imagens mais rápida e mais precisa.',
    noEngine:
      'Esta instalação não consegue ler imagens, então imagens e digitalizações são puladas seja qual for a escolha aqui.',
    appliesFrom:
      'Vale para documentos adicionados ou lidos de novo de agora em diante; os demais ficam com o que já foi lido até serem lidos de novo.',
    choices: {
      auto: {
        label: 'Ler quando parecerem conteúdo',
        hint: 'Páginas digitalizadas e imagens grandes o bastante para carregar texto são lidas; ícones e logotipos são pulados.'
      },
      always: {
        label: 'Ler todas as imagens',
        hint: 'Mais lento em apresentações cheias de imagens, e vale a pena para capturas de tabelas.'
      },
      off: {
        label: 'Só texto',
        hint: 'Digitalizações e imagens são contadas, mas nunca lidas. Um PDF só de digitalização não pode ser indexado.'
      }
    },
    describe: {
      auto: (languages) => `lidas quando parecerem conteúdo${languages}`,
      always: (languages) => `todas as imagens lidas${languages}`,
      off: 'só texto'
    }
  },

  outdated: {
    readAgain: (fmt, count) =>
      `Ler de novo ${
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento desatualizado`
          : `${fmt.formatCount(count)} documentos desatualizados`
      }`,
    queueing: 'Enfileirando…',
    queued: (fmt, count) => `${documents(fmt, count)} na fila`
  },

  health: {
    // Neutros em gênero e número de propósito: cada um aparece depois de um número, e "3 não foi
    // possível ler" seria agramatical num espaço estreito demais para uma oração inteira.
    answering: 'respondendo',
    working: 'em leitura',
    silent: 'sem trechos',
    unreadable: 'com falha',
    quarantined: 'em quarentena',

    nothingYet: 'Nada aqui ainda. Um agente perguntado sobre isso não vai encontrar nada para citar.',
    allAnswering: (fmt, total, held) =>
      isOne(fmt, total)
        ? `${fmt.formatCount(total)} documento respondendo, em ${passages(fmt, held)}.`
        : `Todos os ${fmt.formatCount(total)} documentos respondendo, em ${passages(fmt, held)}.`,
    nothingCited: 'Nada aqui pode ser citado ainda.',
    ratio: (fmt, answering, total, held) =>
      `${fmt.formatCount(answering)} de ${fmt.formatCount(total)} documentos respondendo, em ${passages(fmt, held)}.`,
    problem: {
      unreadable: (fmt, count) =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento não pôde ser lido`
          : `${fmt.formatCount(count)} documentos não puderam ser lidos`,
      quarantined: (fmt, count) =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento foi colocado em quarentena`
          : `${fmt.formatCount(count)} documentos foram colocados em quarentena`,
      silent: (fmt, count) =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento não tem trechos`
          : `${fmt.formatCount(count)} documentos não têm trechos`,
      working: (fmt, count) =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento ainda está em leitura`
          : `${fmt.formatCount(count)} documentos ainda estão em leitura`
    },
    summary: (fmt, lead, problems) => `${lead} ${fmt.formatList(problems)}.`
  },

  search: {
    label: 'Buscar nesta base de conhecimento',
    placeholder: 'Pergunte o que perguntariam a um agente…',
    run: 'Buscar',
    running: 'Buscando…',
    hint: 'A mesma busca que um agente faz nesta base de conhecimento, como você. Abra um resultado para ver o documento de onde ele veio.',
    hintMaintainer:
      'A mesma busca que um agente faz nesta base de conhecimento, como você. “Como foi classificado” mostra como cada trecho foi encontrado.',
    ranked: 'Como foi classificado',
    rankedLead:
      'Cada trecho é encontrado pela correspondência das palavras, pela correspondência do significado, ou pelas duas, e as duas classificações são combinadas em uma só ordem.',
    strategyLabel: 'O que comparar',
    strategies: { hybrid: 'palavras e significado', semantic: 'só significado', lexical: 'só palavras' },
    readouts: { time: 'levou', words: 'por palavras', meaning: 'por significado', returned: 'retornou' },
    byWords: (rank) => `palavras nº ${rank}`,
    byMeaning: (rank) => `significado nº ${rank}`,
    combined: (score) => `combinado ${score}`,
    noResults: 'Nada correspondeu',
    noResultsBody:
      'Nem a correspondência das palavras nem a do significado encontraram um trecho aqui. Tente as palavras que os próprios documentos usam.',
    results: (fmt, count) => passages(fmt, count),
    failed: 'A busca não foi executada.'
  },

  access: {
    title: 'Quem pode usar',
    lead: 'As pessoas que podem usar conseguem encontrar, abrir e buscar nela, e recebem respostas a partir dela por qualquer agente que usem.',
    sectionLead:
      'Os grupos que você gerencia entram no ar quando você salva. Todas as pessoas, ou um grupo que você não gerencia, aguardam a revisão de um administrador — o resto da mudança entra no ar mesmo assim.',
    sectionLeadAdmin: 'Você tem o papel de administrador, então o que você escolher entra no ar quando salvar.',
    loading: 'Carregando quem pode usar…',
    failed: 'Não foi possível carregar quem pode usar.',
    failedBody: 'Nada mudou. Tente de novo em instantes.',
    onlyPerson: (name) => `Só ${name}`,
    save: 'Salvar',
    sendForReview: 'Enviar para revisão',
    saveAndSend: 'Salvar e enviar para revisão',
    saving: 'Salvando…',
    discard: 'Descartar alterações',
    foldersAskAgain: (fmt, count) =>
      isOne(fmt, count)
        ? 'A pasta conectada dela vai pedir que você confirme quem pode buscar no que ela carrega antes da próxima verificação.'
        : `As ${fmt.formatCount(count)} pastas conectadas dela vão pedir que você confirme quem pode buscar no que elas carregam antes da próxima verificação.`,
    foldersAskAfterApproval: (fmt, count) =>
      isOne(fmt, count)
        ? 'Se um administrador aprovar, a pasta conectada dela vai pedir que você confirme quem pode buscar no que ela carrega antes da próxima verificação.'
        : `Se um administrador aprovar, as ${fmt.formatCount(count)} pastas conectadas dela vão pedir que você confirme quem pode buscar no que elas carregam antes da próxima verificação.`,
    saved: 'Salvo. Está no ar agora.',
    nothingChanged: 'Nada mudou — já é isso que define quem pode usar.',
    sentForReview: 'Enviado para revisão.',
    savedAndSent: 'Salvo — o que você podia mudar já está no ar.',
    waitingFor: 'Aguardando revisão:',
    waitingEveryone: 'compartilhar com todas as pessoas',
    reviewNote: 'Um administrador decide, e você recebe um aviso de qualquer jeito.',
    stale: 'Alguém mudou quem pode usar enquanto você editava. A página foi recarregada — confira e salve de novo.',
    pendingTitle: 'Aguardando revisão',
    // `when` chega pronto e de duas formas: relativa, com preposição e com o ponto da
    // abreviação ("há 5 min."), ou absoluta e sem preposição ("21 de ago."). Por isso ele entra
    // depois de dois-pontos e no meio da frase: assim nenhuma das duas formas precisa de uma
    // preposição daqui, e o ponto que ela traz não vira "min.." no fim (LOC-022).
    pendingBody: (when) => `Pedido: ${when} — até um administrador decidir, quem pode usar é o que aparece abaixo.`,
    pendingEveryone: 'Compartilhar com todas as pessoas',
    pendingGroups: 'Compartilhar com',
    withdraw: 'Retirar solicitação',
    withdrawing: 'Retirando…',
    withdrawn: 'Solicitação retirada. Nada mudou sobre quem pode usar.',
    sharedNow: 'Compartilhada agora',
    sharedBy: (name, byAdministrator, when) =>
      `${name ? `Compartilhada por ${name}${byAdministrator ? ', no papel de administrador' : ''}` : 'Compartilhada'} · ${when}`,
    approvedBy: (name) => `aprovada por ${name}`
  },

  agents: {
    title: 'Agentes que usam',
    lead: 'Um agente responde a partir dela só para as pessoas que podem usar — a menos que você deixe o agente responder para todas as pessoas que ele alcança.',
    sectionLead:
      'Todos os agentes a que ela está ligada: quantas pessoas cada um alcança, e quantas delas não podem usar esta base de conhecimento.',
    loading: 'Carregando os agentes que usam…',
    failed: 'Não foi possível carregar os agentes que usam.',
    empty: 'Nenhum agente a usa ainda.',
    emptyBody: 'Quando alguém ligar a base a um agente que estiver criando, o agente aparece aqui.',
    maintainedBy: (owner) => `Mantido por ${owner}`,
    // "quem gerencia", como em `groups`, `knowledgeFolders` e `marketplace` — *gestor/gestora*
    // é flexionado e esta linha não sabe quem são essas pessoas (D-21).
    groupManagers: (group) => `quem gerencia ${group}`,
    state: {
      draft: 'Rascunho',
      published: '',
      archived: 'Descontinuado',
      suspended: 'Pausado por um administrador'
    },
    reaches: (fmt, reach, beyond, base) =>
      beyond === 0
        ? `Alcança ${people(fmt, reach)} — todas podem usar ${base}.`
        : `Alcança ${people(fmt, reach)} — ${fmt.formatCount(beyond)} delas não podem usar ${base} por conta própria.`,
    notLive: (state) =>
      state === 'archived'
        ? 'Descontinuado — ninguém pode começar uma conversa com ele.'
        : state === 'suspended'
          ? 'Pausado por um administrador — ninguém pode usar agora.'
          : 'Ainda não compartilhado com ninguém.',
    forEveryone: 'Responde a partir dela para todas as pessoas que alcança',
    onlyUsers: 'Responde a partir dela só para quem pode usar',
    allowedBy: (name, byAdministrator, when) =>
      `${name ? `permitido por ${name}${byAdministrator ? ', no papel de administrador' : ''}` : 'permitido'} · ${when}`,
    detached: 'Ele não usa mais esta base de conhecimento. A permissão não faz nada até ele ser ligado de novo.',
    letAnswer: 'Deixar responder para todas as pessoas que alcança',
    stop: 'Parar',
    stopConfirm: (agent, base) =>
      `Parar ${agent} de responder a partir de ${base} para todas as pessoas que alcança? Da próxima mensagem em diante, quem não pode usar ${base} deixa de receber respostas dela. As respostas já dadas não são recolhidas.`,
    stopping: 'Parando…',
    keep: 'Manter',
    asked: (name, origin) =>
      `${name} pediu que você deixe o agente responder para todas as pessoas que ele alcança${origin === 'maker' ? ', enquanto criava o agente' : ' — essa pessoa usa o agente, e ele não conseguiu responder a ela a partir desta base'}.`,
    allow: 'Permitir',
    decline: 'Recusar',
    forAgent: (agent) => ` — ${agent}`,
    requestLabel: (name) => `Solicitação de ${name}`,
    waiting: (fmt, count) =>
      isOne(fmt, count)
        ? 'Alguém está esperando você decidir se um agente pode responder a partir dela para todas as pessoas que ele alcança.'
        : `${fmt.formatCount(count)} pessoas estão esperando você decidir se um agente pode responder a partir dela para todas as pessoas que ele alcança.`,
    showRequests: 'Mostrar',
    decided: {
      // Sem ponto final: a linha termina no `when`, que já traz o ponto da abreviação
      // ("há 5 min.", "20 de set."), e um segundo ponto vira "min.." (LOC-022).
      approved: (asker, by, when) =>
        `A solicitação de ${asker} já foi decidida: permitida${by ? ` por ${by}` : ''} · ${when}`,
      declined: (asker, by, when) =>
        `A solicitação de ${asker} já foi decidida: recusada${by ? ` por ${by}` : ''} · ${when}`,
      withdrawn: (asker) => `${asker} retirou esta solicitação, então não há o que decidir.`,
      closed: (asker, reason) => `A solicitação de ${asker} foi encerrada — ${reason}.`,
      detached: (agent) =>
        `Esta solicitação continua aberta, mas ${agent} não usa mais esta base de conhecimento, então não há o que permitir.`,
      gone: 'Essa solicitação não está mais aberta, então não há o que decidir.'
    },
    closedReason: {
      base_deleted: 'a base de conhecimento foi excluída',
      agent_retired: 'o agente foi descontinuado',
      group_deleted: 'um grupo do qual ela dependia foi excluído',
      no_longer_eligible: 'ela não se aplica mais'
    }
  },

  lend: {
    title: (agent) => `Deixar ${agent} responder para todas as pessoas que alcança?`,
    allowTitle: (name, agent) => `Permitir a solicitação de ${name} para ${agent}?`,
    lead: (agent, base) =>
      `Hoje ${agent} responde a partir de ${base} só para as pessoas que podem usar ${base}. Isto permite que ele responda a partir dela para todas as pessoas que alcança.`,
    maintainedBy: (agent, owner) => lendMaintainedBy(agent, owner),
    acts: LEND_ACTS,
    delegates: LEND_DELEGATES,
    actsAndDelegates: LEND_ACTS_AND_DELEGATES,
    answersOnly: LEND_ANSWERS_ONLY,
    reachNow: (fmt, reach, beyond, base) => lendReachNow(fmt, reach, beyond, base),
    reachNotLive: (base) => lendReachNotLive(base),
    grows: (base) => lendGrows(base),
    recall: LEND_RECALL,
    disclosure: (fmt, ack) => [
      lendMaintainedBy(ack.agent, { kind: ack.ownerKind, name: ack.ownerName }),
      lendCapability(ack.acts, ack.delegates),
      ack.live ? lendReachNow(fmt, ack.reach, ack.beyond, ack.base) : lendReachNotLive(ack.base),
      lendGrows(ack.base),
      LEND_RECALL
    ],
    note: (name) => `${name} escreveu:`,
    confirm: 'Deixar responder',
    allow: 'Permitir',
    working: 'Salvando…',
    cancel: 'Cancelar'
  },

  decline: {
    title: (name) => `Recusar a solicitação de ${name}?`,
    lead: (agent, base, name) =>
      `${agent} continua respondendo a partir de ${base} só para as pessoas que podem usar. ${name} recebe um aviso, com a sua nota.`,
    note: 'Nota',
    noteHint: 'Obrigatório — diga por quê, para que a pessoa possa agir.',
    submit: 'Recusar',
    working: 'Recusando…',
    cancel: 'Cancelar'
  },

  limits: {
    unlimited: 'Suas bases de conhecimento não têm limite, porque você tem o papel de administrador.',
    you: (fmt, used, limit) => `Você tem ${fmt.formatCount(used)} de ${bases(fmt, limit)}.`,
    group: (fmt, group, used, limit) => `${group} tem ${fmt.formatCount(used)} de ${bases(fmt, limit)}.`,
    perBase: (fmt, docs, space) => `Cada uma pode guardar ${documents(fmt, docs)} e ${space}.`,
    holdsAndPerBase: (holds, perBase) => `${holds} ${perBase}`,
    youFull: (fmt, limit) =>
      `Você já tem ${bases(fmt, limit)}, o máximo que pode ter. Exclua uma de que não precisa mais, ou peça a quem administra para aumentar o limite.`,
    groupFull: (fmt, group, limit) =>
      `${group} já tem ${bases(fmt, limit)}, o máximo que pode ter. Escolha a quem ela vai pertencer, ou peça a quem administra para aumentar o limite.`,
    unknown: 'Valem os limites de quem receber. Se isto passar do limite, é recusado e nada muda.',
    meter: 'Bases de conhecimento',
    meterValue: (fmt, used, limit) => `${fmt.formatCount(used)} de ${fmt.formatCount(limit)}`
  },

  create: {
    title: 'Nova base de conhecimento',
    lead: 'Um lugar para os documentos que um agente pode citar. No começo, só quem a mantém pode usar.',
    name: 'Nome',
    namePlaceholder: 'Políticas de viagem',
    nameHint: 'Até 120 caracteres. As pessoas veem isto onde quer que ela seja compartilhada.',
    description: 'Descrição (opcional)',
    descriptionPlaceholder: 'O que um agente vai encontrar aqui',
    afterwards: 'Você escolhe quem mais pode usar depois, na aba “Quem pode usar”.',
    submit: 'Criar base de conhecimento',
    submitting: 'Criando…',
    cancel: 'Cancelar',
    forPerson: (name) =>
      `Ela é criada como sua e depois passada para ${name}. Valem os limites dessa pessoa; se isto passar do limite, ela continua sendo sua.`,
    notMoved: (name, reason) =>
      `Ela foi criada, mas não pôde ser passada para ${name}: ${reason} Por enquanto ela é sua — mova a partir da página dela.`,
    open: 'Abrir'
  },

  move: {
    title: (name) => `Mover ${name}`,
    lead: 'Mover nunca muda quem pode usar. Valem os limites de quem receber.',
    maintainers: {
      you: 'Você vai mantê-la.',
      person: (name) => `${name} vai mantê-la.`,
      group: (group) => `Quem gerencia ${group} vai mantê-la.`
    },
    overLimit: (fmt, docs, space) =>
      `Ela tem mais do que o novo limite permite (${documents(fmt, docs)} e ${space}), então nada novo pode ser adicionado até que parte disso seja removida.`,
    folders:
      'As pastas conectadas dela vão pedir a quem passar a mantê-la que confirme quem pode buscar no que elas carregam antes da próxima verificação.',
    unknown: 'Valem os limites de quem receber. Se isto passar do limite, a mudança é recusada e nada muda.',
    submit: 'Mover',
    submitting: 'Movendo…',
    cancel: 'Cancelar',
    choose: 'Escolha para onde ela vai.'
  },

  edit: {
    title: (name) => `Editar ${name}`,
    lead: 'Renomear nunca muda quem pode usar nem quais agentes usam.',
    submit: 'Salvar',
    submitting: 'Salvando…',
    cancel: 'Cancelar'
  },

  remove: {
    title: (name) => `Excluir ${name}?`,
    lead: 'Isto não pode ser desfeito. Leia o que muda antes de confirmar.',
    documents: (fmt, count) =>
      count === 0
        ? 'Ela não tem documentos.'
        : isOne(fmt, count)
          ? `${fmt.formatCount(count)} documento dela para de responder, para todo mundo, e não pode ser recuperado por aqui.`
          : `Os ${fmt.formatCount(count)} documentos dela param de responder, para todo mundo, e não podem ser recuperados por aqui.`,
    agents: 'Estes agentes a usam. Continuam funcionando sem ela, e quem os mantém recebe um aviso:',
    noAgents: 'Nenhum agente a usa.',
    folders: (fmt, count) =>
      isOne(fmt, count)
        ? 'A pasta conectada dela para de ler, e nada no lugar de onde vêm os arquivos é tocado.'
        : `As ${fmt.formatCount(count)} pastas conectadas dela param de ler, e nada nos lugares de onde vêm os arquivos é tocado.`,
    requests: 'As solicitações sobre ela são encerradas, e quem pediu recebe um aviso.',
    typeName: (name) => `Digite ${name} para confirmar`,
    submit: 'Excluir base de conhecimento',
    submitting: 'Excluindo…',
    cancel: 'Cancelar'
  },

  drawer: {
    label: (title) => `${title} — detalhes do documento`,
    closeScrim: 'Fechar os detalhes do documento',
    removeFooter: 'Remover tira os trechos dele de toda resposta futura.',
    actionOn: (action, title) => `${action}: ${title}`,

    answering: (fmt, count) => `Respondendo. ${passages(fmt, count)} que um agente pode citar.`,
    beingRead: (status) => `Em leitura — no momento: ${status}. Nada deste arquivo pode ser citado ainda.`,
    silentTitle: 'Indexado, mas sem nenhum trecho.',
    silentBody:
      'Este arquivo está no catálogo e não pode ser citado — a recuperação lê trechos, e este não tem nenhum. Adicione de novo; se ele chegar do mesmo jeito, o arquivo não tem texto que o leitor consiga alcançar.',
    quarantinedTitle: 'Separado: ele carrega conteúdo ativo.',
    quarantinedBody:
      'Ele nunca foi lido e nada dele foi indexado. Macros, scripts embutidos e ações de inicialização são o que isto pega; tire tudo isso na origem — imprimir para um novo PDF ou salvar sem macros costuma resolver — e adicione a cópia limpa. Não há como ler esta cópia do jeito que ela está.',
    fileKept:
      'O arquivo é mantido. Assim que a causa for resolvida — OCR ligado, o motor instalado, um leitor mais novo no ar — “Ler de novo” abaixo relê esta cópia; um arquivo corrigido é adicionado como qualquer outro.',
    reading: 'Lendo…',
    readAgain: 'Ler de novo',

    whatWasRead: 'O que foi lido',
    readouts: {
      structure: 'estrutura',
      pictures: 'imagens',
      ocr: 'ocr',
      notRead: 'não lido',
      readIn: 'lido em',
      searchLanguage: 'idioma da busca'
    },
    proseOnly: 'só texto corrido',
    noPictures: 'nenhuma',
    structure: {
      headings: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'título' : 'títulos'}`,
      tables: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'tabela' : 'tabelas'}`,
      listItems: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'item de lista' : 'itens de lista'}`,
      notes: (fmt, count) =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'nota do apresentador' : 'notas do apresentador'}`,
      pages: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'página' : 'páginas'}`,
      slides: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'slide' : 'slides'}`,
      sheets: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'planilha' : 'planilhas'}`
    },
    joinParts: (parts) => parts.join(', '),
    pictures: (found, withText, confidence) =>
      `${found} encontradas · ${withText} com texto${confidence === null ? '' : ` · ${confidence} de certeza`}`,
    ocrValue: (engine, languages, scannedPages) =>
      `${engine} (${languages})${scannedPages === null ? '' : ` · ${scannedPages}`}`,
    scannedPages: (fmt, count) =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'página digitalizada' : 'páginas digitalizadas'}`,
    hiddenSlides: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'slide oculto' : 'slides ocultos'}`,
    hiddenSheets: (fmt, count) =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'planilha oculta' : 'planilhas ocultas'}`,
    // "imagem" é feminino, então os particípios concordam: "1 não lida", "4 não lidas".
    skipped: {
      tiny: (fmt, count) =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'pequena demais' : 'pequenas demais'} para ter texto`,
      duplicate: (fmt, count) => `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'repetida' : 'repetidas'}`,
      unsupported: (fmt, count) => `${fmt.formatCount(count)} em um formato que o OCR não abre`,
      off: (fmt, count) =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'não lida' : 'não lidas'} (o OCR está desligado nesta base de conhecimento)`,
      unavailable: (fmt, count) =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'não lida' : 'não lidas'} (nenhum motor de OCR instalado)`,
      cap: (fmt, count) => `${fmt.formatCount(count)} além do limite por documento`
    },
    picturesSkipped: (reasons) => `Imagens puladas: ${reasons.join('; ')}.`,
    discarded: (fmt, count) =>
      isOne(fmt, count)
        ? `${fmt.formatCount(count)} bloco de texto reconhecido descartado por ser incerto demais para indexar.`
        : `${fmt.formatCount(count)} blocos de texto reconhecido descartados por serem incertos demais para indexar.`,

    aConnectedFolder: 'uma pasta conectada',
    folderGone: 'A pasta de onde ele veio não está mais conectada; o documento fica até alguém remover.',

    lineage: 'Procedência',
    lineageLabels: {
      status: 'status',
      size: 'tamanho',
      passages: 'trechos',
      type: 'tipo',
      pages: 'páginas',
      language: 'idioma',
      author: 'autoria',
      written: 'escrito',
      parser: 'parser',
      embedding: 'embedding',
      pipeline: 'pipeline',
      contentHash: 'hash do conteúdo',
      added: 'adicionado',
      indexed: 'indexado'
    },
    undetected: 'não detectado',
    unstamped: 'sem carimbo',
    pipelineOutdated: (version) => `${version} · desatualizado`,

    passagesHeading: 'Trechos',
    passageCount: (fmt, count) => passages(fmt, count),
    matchCount: (fmt, matched, total) => `${fmt.formatCount(matched)} de ${fmt.formatCount(total)} correspondem`,
    findLabel: 'Buscar nos trechos',
    findPlaceholder: 'Buscar nos trechos…',
    loadingPassages: 'Lendo os trechos…',
    passagesFailed: 'Não foi possível ler os trechos.',
    noPassages: 'Nenhum trecho',
    noPassagesBody: 'Nada foi indexado deste arquivo, então não há nada que um agente pudesse citar dele.',
    noMatch: 'Nenhum trecho contém esse texto.',
    tokens: (fmt, count) => `${fmt.formatCount(count)} tok`,
    showMore: (fmt, more, total) => `Mostrar mais ${fmt.formatCount(more)} de ${fmt.formatCount(total)}`,
    capped: (fmt, limit) =>
      `Os primeiros ${fmt.formatCount(limit)} trechos deste documento. O que passar disso está indexado e não aparece aqui.`
  },

  genericError: 'Algo deu errado. Tente de novo.'
}
